import api from './api';
import Cookies from 'js-cookie';

// Cookie helper functions using js-cookie library
const setCookie = (name, value, days = 7) => {
  Cookies.set(name, value, {
    expires: days,
    path: '/',
    sameSite: 'Lax',
  });
};

const getCookie = (name) => {
  return Cookies.get(name);
};

const deleteCookie = (name) => {
  Cookies.remove(name, { path: '/' });
};

const authService = {
  // Register new user
  register: async (email, password, fullName, registration = {}) => {
    try {
      const response = await api.post('/api/auth/register', {
        email,
        password,
        ...(fullName && { fullName }),
        challengeId: registration.challengeId,
        verificationCode: registration.verificationCode,
        agreed: registration.agreed,
      });

      const token = response.data.data?.accessToken;
      const user = response.data.data?.user;

      if (token) {
        setCookie('token', token, 7);
        localStorage.setItem('token', token);
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
        }
      }

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Registration failed';
      throw new Error(errorMessage);
    }
  },

  // Verify email with OTP
  verifyEmail: async (email, code) => {
    try {
      const response = await api.post('/api/auth/verify-email', {
        email,
        code,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Verification failed' };
    }
  },

  // Resend verification code
  resendVerification: async (email) => {
    try {
      const response = await api.post('/api/auth/resend-verification', {
        email,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to resend code' };
    }
  },

  // Login user
  login: async (email, password) => {
    try {
      const response = await api.post('/api/auth/login', {
        email,
        password,
      });

      // Backend sends: response.data.data.accessToken
      const token = response.data.data?.accessToken;
      const user = response.data.data?.user;

      if (token) {
        // Set cookie with token from API
        setCookie('token', token, 7);

        // Save in localStorage (backup)
        localStorage.setItem('token', token);

        // Store user info
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
        }
      }

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Login failed';
      throw new Error(errorMessage);
    }
  },

  // Logout user
  logout: async () => {
    // If there's no token, just clear anything leftover and return gracefully
    const existingToken = getCookie('token') || localStorage.getItem('token');
    if (!existingToken) {
      deleteCookie('token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return { success: true, message: 'Already logged out' };
    }

    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      // Swallow API errors: user experience should still be a clean logout
    } finally {
      // ALWAYS clear local session data
      deleteCookie('token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }

    return { success: true };
  },

  // Get current user
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Check if user is authenticated (check both cookie and localStorage)
  isAuthenticated: () => {
    return !!getCookie('token') || !!localStorage.getItem('token');
  },

  // Get token (try cookie first, then localStorage)
  getToken: () => {
    return getCookie('token') || localStorage.getItem('token');
  },

  // Fetch current profile from backend
  getProfile: async () => {
    try {
      const response = await api.get('/api/auth/profile');
      const user = response.data?.data || null;
      if (user) {
        // Keep local cache in sync
        localStorage.setItem('user', JSON.stringify(user));
      }
      return user;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || 'Failed to load profile'
      );
    }
  },

  // Update profile (basic preliminary verification fields or FormData for image upload)
  updateProfile: async (data) => {
    try {
      const isFormData = data instanceof FormData;
      const config = isFormData
        ? {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        : {};

      const response = await api.put('/api/auth/profile', data, config);
      const updated = response.data?.data || null;
      if (updated) {
        localStorage.setItem('user', JSON.stringify(updated));
      }
      return updated;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || 'Failed to update profile'
      );
    }
  },

  // Upload KYC documents (Advanced Verification)
  uploadKYC: async (formData) => {
    try {
      const response = await api.post('/api/kyc/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || 'Failed to upload KYC documents'
      );
    }
  },

  // Get KYC status
  getKYCStatus: async () => {
    try {
      const response = await api.get('/api/kyc/status');
      return response.data?.data || response.data || null;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || 'Failed to fetch KYC status'
      );
    }
  },
};

export default authService;
